import React, { useState, useMemo, useEffect } from 'react'
import {
  ActionButton,
  ActionButtonIcons,
  Button,
  DataTable,
  Modal,
  Search,
  Select,
  type DataTableColumn,
  type DataTableRow,
  type SelectOption,
} from '@enamelai/design-system'
import { attachmentTypeOptions } from '@utils/claims.util'
import {
  MOCK_CLAIMS,
  MOCK_PROCEDURES_BY_CLAIM,
} from './AttachToClaimsModal.mock'
import './AttachToClaimsModal.css'

export type ClaimRow = {
  id: string
  dateSubmitted: string
  patientName: string
  carrier: string
  status: string
}
export type ProcedureRow = {
  id: string
  procedureCode: string
  procedureDate: string
  area: string
  claimAmt: string
  selectActive: boolean
}

export type ProcedureItemRow = {
  id: string
  itemNo: number
  formatType: string
  type: string
  dateTaken: string
}

export type AttachToClaimsModalProps = {
  show: boolean
  onHide: () => void
  fileId?: string | null
  fileName?: string
  fileExtension?: string
  onAttach?: (claimId: string, claim: ClaimRow, type: string) => void
  onAttachToProcedure?: (claimId: string, claim: ClaimRow) => void
  onAttachToProcedureItem?: (
    claimId: string,
    claimItemId: string,
    procedureCode: string,
    itemNo: number,
    patientName: string,
    type: string,
  ) => void
  claims?: ClaimRow[]
  proceduresByClaimId?: Record<string, ProcedureRow[]>
  procedureItemsByCode?: Record<string, ProcedureItemRow[]>
}

const CLAIM_ATTACHMENT_ORDER = ['B4', 'DA', 'DG', 'EB', 'RR', 'XP', 'OZ']
const PROCEDURE_ATTACHMENT_ORDER = ['03', 'RB', 'P6', 'OZ']

const claimAttachmentOptions: SelectOption[] = attachmentTypeOptions
  .filter((opt) => !['03', 'RB', 'P6'].includes(opt.value))
  .sort(
    (a, b) =>
      CLAIM_ATTACHMENT_ORDER.indexOf(a.value) -
      CLAIM_ATTACHMENT_ORDER.indexOf(b.value),
  )

const procedureAttachmentOptions: SelectOption[] = attachmentTypeOptions
  .filter((opt) => PROCEDURE_ATTACHMENT_ORDER.includes(opt.value))
  .sort(
    (a, b) =>
      PROCEDURE_ATTACHMENT_ORDER.indexOf(a.value) -
      PROCEDURE_ATTACHMENT_ORDER.indexOf(b.value),
  )

type PagedTableProps<T extends DataTableRow> = {
  columns: DataTableColumn<T>[]
  rows: T[]
  pageSizeOptions: number[]
  emptyTitle: string
}

function PagedTable<T extends DataTableRow>({
  columns,
  rows,
  pageSizeOptions,
  emptyTitle,
}: PagedTableProps<T>): React.ReactElement {
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const totalPages = Math.max(1, Math.ceil(rows.length / pageSize))
  const currentPage = Math.min(page, totalPages)
  const pageRows = rows.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize,
  )

  return (
    <DataTable<T>
      className="attach-to-claims-table"
      columns={columns}
      rows={pageRows}
      state={rows.length === 0 ? 'empty' : 'default'}
      emptyState={{ title: emptyTitle }}
      pagination={{
        currentPage,
        totalPages,
        pageSize,
        pageSizeOptions,
        onPageChange: setPage,
        onPageSizeChange: (size) => {
          setPageSize(size)
          setPage(1)
        },
      }}
    />
  )
}

const AttachToClaimsModal: React.FC<AttachToClaimsModalProps> = ({
  show,
  onHide,
  fileId,
  fileName,
  fileExtension,
  onAttach,
  onAttachToProcedure,
  onAttachToProcedureItem,
  claims: claimsProp,
  proceduresByClaimId: proceduresByClaimIdProp,
}) => {
  const [searchTerm, setSearchTerm] = useState('')
  const [selectedClaimForProcedure, setSelectedClaimForProcedure] =
    useState<ClaimRow | null>(null)
  const [selectedProcedure, setSelectedProcedure] =
    useState<ProcedureRow | null>(null)
  const [claimAttachTypes, setClaimAttachTypes] = useState<
    Record<string, string>
  >({})
  const [procedureItemTypes, setProcedureItemTypes] = useState<
    Record<string, string>
  >({})

  const proceduresByClaimId =
    proceduresByClaimIdProp ?? MOCK_PROCEDURES_BY_CLAIM

  useEffect(() => {
    if (!show) {
      setSelectedClaimForProcedure(null)
      setSelectedProcedure(null)
      setClaimAttachTypes({})
      setProcedureItemTypes({})
    }
  }, [show])

  const claims = claimsProp ?? MOCK_CLAIMS

  const filteredClaims = useMemo(() => {
    const term = searchTerm.toLowerCase().trim()
    if (!term) return claims
    return claims.filter(
      (c) =>
        c.id.toLowerCase().includes(term) ||
        c.patientName.toLowerCase().includes(term) ||
        c.carrier.toLowerCase().includes(term) ||
        c.status.toLowerCase().includes(term),
    )
  }, [claims, searchTerm])

  const columns: DataTableColumn<ClaimRow>[] = useMemo(
    () => [
      { id: 'id', label: 'Claim ID', width: 120 },
      { id: 'dateSubmitted', label: 'Date submitted', width: 130 },
      { id: 'patientName', label: 'Patient name', width: 150 },
      { id: 'carrier', label: 'Carrier', width: 150 },
      { id: 'status', label: 'Status', width: 130 },
      {
        id: 'attachmentType',
        label: 'Attachment type',
        width: 220,
        renderCell: (row) => (
          <Select
            className="attach-to-claims-select"
            placeholder="Select attachment type"
            options={claimAttachmentOptions}
            value={claimAttachTypes[row.id] ?? ''}
            onChange={(value) =>
              setClaimAttachTypes((prev) => ({
                ...prev,
                [row.id]: String(value),
              }))
            }
          />
        ),
      },
      {
        id: 'actions',
        label: 'Actions',
        align: 'center',
        width: 240,
        renderCell: (row) => (
          <div className="attach-to-claims-actions">
            <Button
              variant="secondary"
              size="xs"
              onClick={() =>
                onAttach?.(row.id, row, claimAttachTypes[row.id] ?? '')
              }
            >
              Attach
            </Button>
            <Button
              variant="primary"
              size="xs"
              onClick={() => {
                setSelectedClaimForProcedure(row)
                onAttachToProcedure?.(row.id, row)
              }}
            >
              Attach to procedure
            </Button>
          </div>
        ),
      },
    ],
    [onAttach, onAttachToProcedure, claimAttachTypes],
  )

  const procedures =
    selectedClaimForProcedure != null
      ? (proceduresByClaimId[selectedClaimForProcedure.id] ?? [])
      : []
  const procedureItems: ProcedureItemRow[] = useMemo(() => {
    if (!selectedProcedure || !fileId) return []
    return [
      {
        id: fileId,
        itemNo: 1,
        formatType: (fileExtension ?? '').toUpperCase(),
        type: '',
        dateTaken: new Date().toLocaleDateString(),
      },
    ]
  }, [selectedProcedure, fileId, fileExtension])

  const procedureColumns: DataTableColumn<ProcedureRow>[] = useMemo(
    () => [
      {
        id: 'procedureCode',
        label: 'Procedure',
        width: 200,
        renderCell: (row) => (
          <div className="attach-to-claims-procedure-cell">
            <i
              className={`mgc ${row.selectActive ? 'mgc_warning_line attach-to-claims-procedure-icon-active' : 'mgc_file_check_line attach-to-claims-procedure-icon-inactive'} attach-to-claims-procedure-icon`}
              title={row.selectActive ? 'Needs attachment' : 'Complete'}
            />
            <span className="attach-to-claims-procedure-code">
              {row.procedureCode}
            </span>
            <i
              className="mgc mgc_information_fill attach-to-claims-procedure-info"
              title="Info"
            />
          </div>
        ),
      },
      { id: 'procedureDate', label: 'Procedure date', width: 130 },
      { id: 'area', label: 'Area', width: 90 },
      { id: 'claimAmt', label: 'Claim amt.', width: 110 },
      {
        id: 'actions',
        label: 'Actions',
        align: 'center',
        width: 120,
        renderCell: (row) => (
          <Button
            variant="primary"
            size="xs"
            disabled={!row.selectActive}
            onClick={() => setSelectedProcedure(row)}
          >
            Select
          </Button>
        ),
      },
    ],
    [],
  )

  const procedureItemColumns: DataTableColumn<ProcedureItemRow>[] = useMemo(
    () => [
      { id: 'itemNo', label: 'Item #', width: 80 },
      { id: 'formatType', label: 'Format type', width: 120 },
      {
        id: 'type',
        label: 'Type',
        width: 220,
        renderCell: (row) => (
          <Select
            className="attach-to-claims-select"
            placeholder="Select attachment type"
            options={procedureAttachmentOptions}
            value={procedureItemTypes[row.id] ?? row.type}
            onChange={(value) =>
              setProcedureItemTypes((prev) => ({
                ...prev,
                [row.id]: String(value),
              }))
            }
          />
        ),
      },
      { id: 'dateTaken', label: 'Date taken', width: 120 },
      {
        id: 'actions',
        label: 'Actions',
        align: 'center',
        width: 160,
        renderCell: (row) => (
          <div className="attach-to-claims-actions">
            <ActionButton
              tone="danger"
              variant="outline"
              label="Delete"
              icon={ActionButtonIcons.delete()}
            />
            <Button
              variant="primary"
              size="xs"
              onClick={() =>
                selectedClaimForProcedure &&
                selectedProcedure &&
                onAttachToProcedureItem?.(
                  selectedClaimForProcedure.id,
                  selectedProcedure.id,
                  selectedProcedure.procedureCode,
                  row.itemNo,
                  selectedClaimForProcedure.patientName,
                  procedureItemTypes[row.id] ?? '',
                )
              }
            >
              Attach
            </Button>
          </div>
        ),
      },
    ],
    [
      procedureItemTypes,
      selectedClaimForProcedure,
      selectedProcedure,
      onAttachToProcedureItem,
    ],
  )

  return (
    <Modal
      open={show}
      onClose={onHide}
      size="xl"
      title="Attach to claims"
      className="attach-to-claims-modal"
      bodyClassName="attach-to-claims-modal-body"
      primaryLabel=""
      secondaryLabel="Cancel Attachments"
      onSecondaryClick={onHide}
    >
      <Search
        value={searchTerm}
        placeholder="Search claim"
        ariaLabel="Search claim"
        onChange={setSearchTerm}
        onClear={() => setSearchTerm('')}
        className="attach-to-claims-search"
      />

      <PagedTable
        columns={columns}
        rows={filteredClaims}
        pageSizeOptions={[10, 25, 50, 100]}
        emptyTitle="No claims found"
      />

      {selectedClaimForProcedure && (
        <section className="attach-to-claims-procedure-section">
          <h3 className="attach-to-claims-section-title">
            Attaching Files to Claim ID: {selectedClaimForProcedure.id}
          </h3>
          <PagedTable
            columns={procedureColumns}
            rows={procedures}
            pageSizeOptions={[10, 25, 50, 100]}
            emptyTitle="No procedures for this claim"
          />
        </section>
      )}

      {selectedProcedure && (
        <section className="attach-to-claims-procedure-section">
          <h3 className="attach-to-claims-section-title">
            Attaching{fileName ? ` "${fileName}"` : ''} to Procedure code:{' '}
            {selectedProcedure.procedureCode}
          </h3>
          <PagedTable
            columns={procedureItemColumns}
            rows={procedureItems}
            pageSizeOptions={[10, 25, 50, 100]}
            emptyTitle="No items to attach"
          />
        </section>
      )}
    </Modal>
  )
}

export default AttachToClaimsModal
